import { Component, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { Location } from "@angular/common";
import { UserProfileService } from "./userprofile.service";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { SnackBarMessageType, ContentType } from "../../shared/contentTypes";
import { Subscription } from "rxjs";
import { ChangeDetectionStrategy } from "@angular/compiler/src/core";
import { CoreLogService } from "../../services/log.service";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { Router } from "@angular/router";
import { UserHelperService } from "../../services/userHelper.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { WebVoucherService } from "../../services/web_voucher.service";
import { NavigationService } from "../../services/navigation.service";
import { UserProfileNavigationViewModel } from "../../shared/models";
import { UserInfoViewModel } from "../../shared/models/userInfo.model";
import { WebShopService } from "../../services/webShop.service";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { RouterHelperService } from "../../services/routerHelper.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";


@Component({
  selector: "userProfile",
  templateUrl: "./userProfile.component.html",
  styleUrls: ["./userProfile.component.scss"],
  providers: [UserProfileService, OverlaySpinnerService, WebVoucherService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent implements OnInit, OnDestroy {
  public userInfo: UserInfoViewModel = new UserInfoViewModel();
  public currentTabIndex = 0;
  public isTestUser: boolean = false;
  public isItemTagEnabled: boolean = false;
  public userProfileWebvoucherNav: UserProfileNavigationViewModel | null;
  public userProfileBonusSystemNav: UserProfileNavigationViewModel | null;
  private _langSubscriber: Subscription;
  public isSmallView: boolean = false;

  constructor(private _location: Location
    , private _router: Router
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _internationalisationService: InternationalisationService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _navigationService: NavigationService
    , private _routerHelperService: RouterHelperService
    , private _webshopService: WebShopService
    , private _userProfileService: UserProfileService
    , private _userHelperService: UserHelperService
    , private _snackBarService: SnackBarService
    , private _objObsInjectHelper: CircularInjectionHelper
    , _mainnavService: MainnavService
  ) {
    this.isSmallView = _mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("UserProfileComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me.isTestUser = me._userHelperService.IsTestUser;
    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me.hasWebvoucherModule();
    me.getUserInfo();
    me.PushChanges();
  }

  private getUserInfo(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._userProfileService.GetUserInfoViewModel()
      .then(async (userViewModel) => {
        const webshopSettings = await me._webshopService.getWebshopSettingsDataModel();
        if (webshopSettings && webshopSettings.is_itemtag_enabled) {
          me.isItemTagEnabled = webshopSettings.is_itemtag_enabled;
        }
        me.userInfo = userViewModel;
        me.PushChanges();
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("UserProfileComponent.getUserInfo", "GetUserInfoViewModel", e);
        me._snackBarService.ShowErrorMessageFailed(87);
        me._overlaySpinnerService.HideLayoutSpinner();
      });
  }

  public get isAuthInternal(): boolean { return this._objObsInjectHelper.yourAppServer.Auth.isLoginInternal; }

  private hasWebvoucherModule() {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._navigationService.UserProfileNavigation()
      .then(userProfileNavigationViewModel => {
        if (userProfileNavigationViewModel) {
          me.userProfileWebvoucherNav = userProfileNavigationViewModel.find(u => u.contentType === ContentType.WebVoucher);
          me.userProfileBonusSystemNav = me._navigationService.isContenttypeSupported(ContentType.Bonussystem) && userProfileNavigationViewModel.find(u => u.contentType === ContentType.Bonussystem);
          me.PushChanges();
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch((error) => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._coreLogService.error("UserProfileComponent.hasWebvoucherModule", "_navigationService.HasWebvoucherModule", error);
        me._snackBarService.ShowErrorMessageFailed(88);
      });
  }

  public logOut(): void {
    const me = this;
    me._userProfileService.logOut()
      .then((response) => {
        if (response) {
          me._router.navigateByUrl("/");
        } else {
          me._snackBarService.ShowMessage(snackBarMessageKey.failed, SnackBarMessageType.Warning);
        }
      })
      .catch(error => {
        me._coreLogService.error("UserProfileComponent.logOut", "_userProfileService.logOut", error);
        me._snackBarService.ShowErrorMessageFailed(89);
      });
  }

  public navigateToChangePassword(): void {
    const me = this;
    me._router.navigateByUrl("/changepassword");
  }

  /**set route to userprofile
   * @tabIndex set the active tab on loading the page
   */
  public navigationToUserAccount(tabIndex: number = 0): void {
    const me = this;
    me._router.navigate(["/userprofiledetail", tabIndex]);
  }

  /**set Route for navigation 
  *@path the destination path for routing 
  */
  public navigation(path: string): void {
    const me = this;
    me._router.navigateByUrl(`/${path}`);
  }

  /**When small view is true, navigation back by swiping from left to right.*/
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

  public navigateBack(): void {
    const me = this;
    me.checkPreviousUrl();
  }

  public checkPreviousUrl(): void {
    const me = this;
    const previousUrl = me._routerHelperService.getPreviousUrl();
    if (previousUrl == "/registration") {
      me._router.navigate(["/"]);//home
    } else {
      me._location.back();
    }
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
  }

}
